


// Only documented, temporary publication/build conditions may enter the
// dashboard's existing bounded retry protocol. Error-body hints alone cannot
// promote authorization, validation, or unknown server failures.
export function isReportingPendingResponse(status: number, code?: string): boolean {
  return status === 503 && (
    code === 'usage_dashboard_reporting_today_pending'
    || code === 'usage_dashboard_reporting_history_pending'
    || code === 'usage_dashboard_reporting_publication_changed'
    || code === 'usage_dashboard_reporting_calculation_busy'
  );
}

// Keep only a small amount of sensitive activity data in same-tab memory. The
// cache exists solely to make a server-authorized 304 useful; it is never
// persisted or shared across authenticated presentation scopes.
export const USAGE_TIMELINE_HTTP_CACHE_MAX_BYTES = 16 * 1024 * 1024;

/**
 * Bound decoded timeline retention without serializing a second full copy of a
 * response. This intentionally walks unknown fields too: a malformed or
 * unexpectedly expanded server DTO must not bypass the same-tab byte cap.
 */
export function usageTimelineLedgerEstimatedBytes(value: unknown): number {
  const limit = USAGE_TIMELINE_HTTP_CACHE_MAX_BYTES;
  const seen = new WeakSet<object>();
  const textBytes = (text: string): number => {
    if (typeof TextEncoder === 'function') return new TextEncoder().encode(text).byteLength;
    return text.length * 2;
  };
  const visit = (candidate: unknown, depth: number): number => {
    if (depth > 32) return limit + 1;
    if (typeof candidate === 'string') return Math.min(limit + 1, textBytes(candidate) + 2);
    if (typeof candidate === 'number' || typeof candidate === 'boolean' || candidate == null) return 8;
    if (typeof candidate !== 'object') return limit + 1;
    if (seen.has(candidate)) return 0;
    seen.add(candidate);
    let size = 2;
    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        size += visit(item, depth + 1) + 1;
        if (size > limit) return limit + 1;
      }
      return size;
    }
    for (const [key, item] of Object.entries(candidate as Record<string, unknown>)) {
      size += textBytes(key) + visit(item, depth + 1) + 3;
      if (size > limit) return limit + 1;
    }
    return size;
  };
  return visit(value, 0);
}
